import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useJoyride } from 'contexts/joyride';
import { useViewport } from 'contexts/viewport';
import { PageContext } from 'pages/InstructorViewAssignment/PageProvider';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Box,
    Button,
    Divider,
    Icon,
    IconButton,
    ProgressBar,
    ResourceChip,
    Skeleton,
    Stack,
    Steps,
    styled,
    Text,
} from 'shell/components';
import { EXAMPLE_ASSIGNMENT_ID } from 'utils/constants';
import { canAccessFeature, isAssignmentValid as validateAssignment } from 'utils/helper';

const FEATURE = 'batch-evaluate-assignment-submissions';

const PaywallIconPrimary = (
    <Icon
        sx={(theme) => ({
            borderRadius: '50%',
            padding: theme.spacing(0.5),
            background: theme.palette.ensightful.shades[0],
            color: theme.palette.ensightful.primary[500],
        })}
        kind={'starFilled'}
    />
);

const PaywallIconWarning = (
    <Icon
        sx={(theme) => ({
            borderRadius: '50%',
            padding: theme.spacing(0.6),
            background: theme.palette.ensightful.warning[800],
            color: theme.palette.ensightful.shades[0],
        })}
        kind={'starFilled'}
    />
);

const StepOne = ({ isStepFinished, isStepCompleted }) => {
    const { currentAssignment, previewSubmissions } = useApp();
    const { auth } = useAuth();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const {
        setUploadPreviewSubmissionsDialog,
        canCancelBatch,
        isPreviewGenerating,
        detachPreviewFile,
        createPreviewSubmissions,
        cancelBatch,
        startFineTuning,
        proceedToGradingFeedback,
        skipFineTuning,
    } = useContext(PageContext);

    const isAssignmentValid = validateAssignment(currentAssignment);
    const canUpload =
        isAssignmentValid && (currentAssignment.ai_feedback_enabled || currentAssignment.ai_grading_enabled);
    const hasAtLeastOnePreviewSubmission = Boolean(previewSubmissions.length);
    const areAllPreviewFilesUpToDate = currentAssignment.preview_files?.every((pf) => pf.is_preview_up_to_date);
    const canFineTune = hasAtLeastOnePreviewSubmission && areAllPreviewFilesUpToDate;

    const getResourceDescription = (file) => {
        if (file.is_preview_up_to_date) return t('fine-tuning.ready-label', 'Ready for fine-tuning');
        return isPreviewGenerating ? (
            `${t('common:ui.processing', 'Processing')}...`
        ) : (
            <Text color={'light'}>{t('fine-tuning.generate-preview-prompt', 'Please generate a preview')}</Text>
        );
    };

    const handleSelect = ({ key }, file) => {
        if (key === 'delete') {
            detachPreviewFile(file);
        }
    };

    const renderStepHeader = () => {
        return (
            <Stack spacing={2}>
                <Stack
                    alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                    direction={device === 'mobile' ? 'column-reverse' : 'row'}
                    spacing={2}
                >
                    <Text variant={'sh2'} weight={'semibold'}>
                        {t('fine-tuning.title', 'Preview and Fine-Tune')}
                    </Text>
                    <Text component={'i'} color={'light'}>
                        {t('common:ui.recommended', 'Recommended')}
                    </Text>
                </Stack>
                {isStepFinished ? (
                    <Box sx={{ height: '4px' }} />
                ) : (
                    <Text color={'light'}>
                        {t(
                            'fine-tuning.description',
                            'Upload up to three sample submissions to preview grading and feedback, and make adjustments to align with your criteria. For best results, include high, medium, and low-scoring examples. Fine-tuning is optional but highly recommended for better accuracy.'
                        )}
                    </Text>
                )}
            </Stack>
        );
    };

    const handleUploadSampleSubmission = () => {
        setUploadPreviewSubmissionsDialog({ open: true });
    };

    const renderStepBody = () => {
        if (isStepFinished) return null;

        if (!currentAssignment.preview_files?.length) {
            return (
                <Button
                    sx={{ width: '100%' }}
                    disabled={!canUpload || auth.role === 'ta'}
                    startIcon={<Icon kind={'upload'} />}
                    tooltip={
                        !isAssignmentValid
                            ? {
                                  component: t(
                                      'fine-tuning.invalid-assignment-tooltip',
                                      'Fine-tuning is unavailable because this assignment is missing required information. Please add a name, description, and rubric to enable fine-tuning.'
                                  ),
                              }
                            : !canUpload
                              ? {
                                    component: t(
                                        'fine-tuning.enable-grading-tooltip',
                                        'Enable grading and/or feedback to upload submissions.'
                                    ),
                                }
                              : undefined
                    }
                    onClick={() => handleUploadSampleSubmission()}
                >
                    {t('fine-tuning.upload-sample-submissions', 'Upload Sample Submissions')}
                </Button>
            );
        }

        return (
            <CustomStack device={device} spacing={4}>
                <Stack spacing={2}>
                    <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Text>{t('fine-tuning.test-submissions', 'Test Submissions')}</Text>
                        <Button
                            disabled={
                                !canUpload ||
                                currentAssignment.preview_files.length >= 3 ||
                                isPreviewGenerating ||
                                auth.role === 'ta'
                            }
                            startIcon={<Icon kind={'upload'} />}
                            variant={'borderless'}
                            onClick={() => setUploadPreviewSubmissionsDialog({ open: true })}
                        >
                            {t('common:controls.upload', 'Upload')}
                        </Button>
                    </Stack>
                    <Divider sx={(theme) => ({ borderColor: theme.palette.ensightful.neutral[100] })} />
                </Stack>
                <Stack spacing={3}>
                    {currentAssignment.preview_files.map((file) => (
                        <ResourceChip
                            key={file.id}
                            description={getResourceDescription(file)}
                            options={[
                                {
                                    key: 'delete',
                                    value: t('common:controls.remove', 'Remove'),
                                    disabled: isPreviewGenerating || auth.role === 'ta',
                                },
                            ]}
                            resource={file}
                            size={'medium'}
                            type={'file'}
                            onSelect={handleSelect}
                        />
                    ))}
                </Stack>
                {isPreviewGenerating ? (
                    <Stack spacing={4}>
                        <Button
                            disabled={!canFineTune || auth.role === 'ta'}
                            startIcon={<Icon kind={'wrench'} />}
                            variant={'secondary'}
                            onClick={startFineTuning}
                        >
                            {t('fine-tuning.fine-tune-label', 'Fine-Tune')}
                        </Button>
                        <Stack>
                            <Text>
                                {t('remaining-assignment-count', '{{assignmentStatus}} assignments', {
                                    assignmentStatus: `${previewSubmissions.length}/${currentAssignment.preview_files.length}`,
                                })}
                            </Text>
                            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                                <ProgressBar
                                    aria-label={'fine-tuning progress'}
                                    simulateProgress={{ duration: 30, maxValue: 90 }}
                                    value={Number(
                                        (
                                            (previewSubmissions.length / currentAssignment.preview_files.length) *
                                            100
                                        ).toFixed(0)
                                    )}
                                />
                                <IconButton
                                    aria-label={'cancel fine-tuning'}
                                    disabled={!canCancelBatch || auth.role === 'ta'}
                                    kind={'exit'}
                                    tooltip={
                                        canCancelBatch
                                            ? { component: t('common:controls.cancel', 'Cancel') }
                                            : undefined
                                    }
                                    size={'small'}
                                    variant={'square'}
                                    onClick={cancelBatch}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                ) : canFineTune ? (
                    <Button
                        disabled={auth.role === 'ta'}
                        startIcon={<Icon kind={'wrench'} />}
                        variant={'secondary'}
                        onClick={startFineTuning}
                    >
                        {t('fine-tuning.fine-tune-label', 'Fine-Tune')}
                    </Button>
                ) : (
                    <Button disabled={auth.role === 'ta'} variant={'secondary'} onClick={createPreviewSubmissions}>
                        {t('fine-tuning.generate-preview-label', 'Generate Preview')}
                    </Button>
                )}
            </CustomStack>
        );
    };

    const renderStepActions = () => {
        if (isStepFinished) return null;

        return (
            <Button
                sx={{ alignSelf: 'center', width: 'fit-content' }}
                color={'neutral'}
                disabled={auth.role === 'ta'}
                variant={'borderless'}
                onClick={isStepCompleted ? proceedToGradingFeedback : skipFineTuning}
            >
                {isStepCompleted
                    ? t('fine-tuning.proceed-to-submission', 'Proceed & Upload Final Submissions')
                    : t('fine-tuning.skip-fine-tuning', 'Skip Fine-Tuning')}
            </Button>
        );
    };

    return (
        <Stack spacing={4}>
            {renderStepHeader()}
            {renderStepBody()}
            {renderStepActions()}
        </Stack>
    );
};

const StepTwo = () => {
    const { currentAssignment, batch: allBatches, submissions } = useApp();
    const { auth } = useAuth();
    const history = useHistory();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const {
        loading,
        setUploadSubmissionsDialog,
        canCancelBatch,
        cancelBatch,
        restartPreviewFineTune,
        importAPISubmissions,
        resetSubmissions,
    } = useContext(PageContext);

    const isStepActive =
        typeof currentAssignment.has_finished_preview === 'undefined' ? true : currentAssignment.has_finished_preview;

    if (!isStepActive) {
        return (
            <Text variant={'sh2'} weight={'semibold'}>
                {t('submission.title', 'Final Submission Grading & Feedback')}
            </Text>
        );
    }

    const isAssignmentValid = validateAssignment(currentAssignment);
    const canUpload =
        isAssignmentValid && (currentAssignment.ai_feedback_enabled || currentAssignment.ai_grading_enabled);
    const canEvaluateSubmissions = canAccessFeature(FEATURE);
    const batch = allBatches.filter(({ type }) => type !== 'fine-tune');

    const isEvaluationInProgress = Boolean(batch.length);
    const hasSubmissions = Boolean(submissions.length);
    const areAllSubmissionsGraded = submissions.every(({ grading }) => Boolean(grading));
    const canRestartPreviewFineTune = !isEvaluationInProgress && !hasSubmissions;

    const numSubmissions = submissions.length;
    const numSubmissionsNotGraded = submissions.filter(
        (submission) => (submission.ai || submission.is_failed) && !submission.grading
    ).length;
    const numSubmissionsGraded = submissions.filter(
        (submission) => (submission.ai || submission.is_failed) && submission.grading
    ).length;
    const numSubmissionsInBatchCompleted = submissions.filter(({ batch_id }) => batch_id === batch[0]?.id).length;
    const numSubmissionsInBatchTotal = batch[0]?.submissions?.length;
    const isApi = Boolean(currentAssignment.api_assignment_id);

    const openUploadSubmissionsDialog = () => setUploadSubmissionsDialog({ open: true });

    const navigateToSubmissionsPage = () => history.push(`/assignments/${currentAssignment.id}/submissions`);

    const navigateToGradingPage = () => history.push(`/assignments/${currentAssignment.id}/grading`);

    const UploadSubmissionsButton = (
        <Button
            sx={{ width: '100%' }}
            disabled={!canUpload || (currentAssignment.api_assignment_id && auth.role === 'ta')}
            loading={loading}
            loadingGrow
            startIcon={
                canEvaluateSubmissions ? (
                    <Icon kind={currentAssignment.api_assignment_id ? 'download' : 'upload'} />
                ) : (
                    PaywallIconPrimary
                )
            }
            tooltip={
                currentAssignment.api_assignment_id && auth.role === 'ta'
                    ? {
                          component: t(
                              'submission.instructor-first-import-grading',
                              'An instructor needs to import submissions before you can start grading.'
                          ),
                      }
                    : !isAssignmentValid
                      ? {
                            component: t(
                                'submission.invalid-assignment-tooltip',
                                'Submission grading & feedback is unavailable because this assignment is missing required information. Please add a name, description, and rubric to enable fine-tuning.'
                            ),
                        }
                      : !canUpload
                        ? {
                              component: t(
                                  'submission.enable-grading-tooltip',
                                  'Enable grading and/or feedback to upload submissions.'
                              ),
                          }
                        : undefined
            }
            onClick={currentAssignment.api_assignment_id ? importAPISubmissions : openUploadSubmissionsDialog}
        >
            {isApi
                ? t('submission.import-submissions', 'Import Submissions')
                : t('submission.upload-submissions', 'Upload Submissions')}
        </Button>
    );

    const UploadAdditionalSubmissionsButton = (
        <Button
            disabled={
                !isAssignmentValid ||
                (!currentAssignment.ai_grading_enabled && !currentAssignment.ai_feedback_enabled) ||
                (currentAssignment.api_assignment_id && auth.role === 'ta')
            }
            loading={loading}
            loadingGrow
            startIcon={
                canEvaluateSubmissions ? (
                    <Icon kind={currentAssignment.api_assignment_id ? 'download' : 'upload'} />
                ) : (
                    PaywallIconWarning
                )
            }
            tooltip={
                currentAssignment.api_assignment_id && auth.role === 'ta'
                    ? {
                          component: t(
                              'submission.instructor-first-import-grading',
                              'An instructor needs to import submissions before you can start grading.'
                          ),
                      }
                    : !currentAssignment.ai_grading_enabled && !currentAssignment.ai_feedback_enabled
                      ? {
                            style: { alignSelf: 'center' },
                            component: t(
                                'submission.enable-grading-tooltip',
                                'Enable grading and/or feedback to upload submissions.'
                            ),
                        }
                      : undefined
            }
            variant={'borderless'}
            onClick={currentAssignment.api_assignment_id ? importAPISubmissions : openUploadSubmissionsDialog}
        >
            {device === 'mobile'
                ? isApi
                    ? t('submission.import-submissions', 'Import Submissions')
                    : t('submission.upload-submissions', 'Upload Submissions')
                : isApi
                  ? t('submission.import-additional-submissions', 'Import Additional Submissions')
                  : t('submission.upload-additional-submissions', 'Upload Additional Submissions')}
        </Button>
    );

    const ResetSubmissionsButton = (
        <Button
            disabled={!hasSubmissions}
            startIcon={<Icon kind={'refresh'} />}
            variant={'borderless'}
            color={'destructive'}
            onClick={resetSubmissions}
        >
            {t('submission.reset-submissions', 'Reset Submissions')}
        </Button>
    );

    const AdditionalButtons = (
        <Stack spacing={4}>
            {UploadAdditionalSubmissionsButton}
            {ResetSubmissionsButton}
        </Stack>
    );

    const EvaluationProgress = (
        <Stack>
            <Text color={'light'}>
                {t('remaining-assignment-count', '{{assignmentStatus}} assignments', {
                    assignmentStatus: `${numSubmissionsInBatchCompleted}/${numSubmissionsInBatchTotal}`,
                })}
            </Text>
            <Stack alignItems={'center'} direction={'row'} spacing={4}>
                <ProgressBar
                    aria-label={'submission process progress'}
                    simulateProgress={{ duration: 30, maxValue: 90 }}
                    value={Number(((numSubmissionsInBatchCompleted / numSubmissionsInBatchTotal) * 100).toFixed(0))}
                />
                <IconButton
                    aria-label={'cancel evaluation of submissions'}
                    disabled={!canCancelBatch || (currentAssignment.api_assignment_id && auth.role === 'ta')}
                    kind={'exit'}
                    tooltip={canCancelBatch ? { component: t('common:controls.cancel', 'Cancel') } : undefined}
                    size={'small'}
                    variant={'square'}
                    onClick={cancelBatch}
                />
            </Stack>
        </Stack>
    );

    const ReviewSubmissionsButton = (
        <Button disabled={isEvaluationInProgress || areAllSubmissionsGraded} onClick={navigateToGradingPage}>
            {t('submission.start-reviewing', 'Start Reviewing')}
        </Button>
    );

    const ViewGradedSubmissionsButton = (
        <Button disabled={!hasSubmissions} variant={'secondary'} onClick={navigateToSubmissionsPage}>
            {t('submission.manage-submissions', 'Manage Submissions')}
        </Button>
    );

    return (
        <Stack spacing={5}>
            <Stack spacing={4}>
                <Text variant={'sh2'} weight={'semibold'}>
                    {t('submission.title', 'Final Submission Grading & Feedback')}
                </Text>
                <GreyBackground spacing={3}>
                    {!isEvaluationInProgress && !hasSubmissions ? (
                        <>
                            {UploadSubmissionsButton}
                            <Text color={'light'}>
                                {t(
                                    'submission.description',
                                    'Grades and feedback will be generated immediately after you upload. Please note, editing assignment details after upload is restricted to ensure consistent grades/feedback for all students. If you need to make changes, duplicate the assignment and edit the new version.'
                                )}
                            </Text>
                        </>
                    ) : (
                        <Stack spacing={4}>
                            <WhiteBackground justifyContent={'space-between'} spacing={3}>
                                <Stack direction={'row'} spacing={2}>
                                    <CustomIcon kind={'error'} />
                                    <Text>{t('submission.submission-status', 'Submission Status')}</Text>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text>{t('submission.pending-review', 'Pending review')}</Text>
                                    <Text weight={'semibold'}>{numSubmissionsNotGraded}</Text>
                                </Stack>
                                <Stack direction={'row'} justifyContent={'space-between'}>
                                    <Text>{t('common:ui.completed', 'Completed')}</Text>
                                    <Text>
                                        {numSubmissionsGraded} /{' '}
                                        {typeof numSubmissionsInBatchTotal === 'undefined'
                                            ? numSubmissions
                                            : numSubmissionsInBatchTotal - numSubmissionsInBatchCompleted}
                                    </Text>
                                </Stack>
                            </WhiteBackground>
                            {ReviewSubmissionsButton}
                            {ViewGradedSubmissionsButton}
                            {auth.role !== 'ta' && (
                                <>
                                    <Divider />
                                    {isEvaluationInProgress ? EvaluationProgress : AdditionalButtons}
                                </>
                            )}
                        </Stack>
                    )}
                </GreyBackground>
            </Stack>
            {canRestartPreviewFineTune && (
                <Button
                    sx={{ alignSelf: 'center' }}
                    color={'neutral'}
                    disabled={auth.role === 'ta'}
                    startIcon={<Icon kind={'refresh'} />}
                    variant={'borderless'}
                    onClick={restartPreviewFineTune}
                >
                    {t('fine-tuning.restart', 'Restart Preview/Fine-Tune')}
                </Button>
            )}
        </Stack>
    );
};

const getStepOne = ({ currentAssignment }) => {
    const isStepCompleted = Boolean(currentAssignment.has_seen_preview);
    const isStepFinished =
        typeof currentAssignment.has_finished_preview === 'undefined' ? true : currentAssignment.has_finished_preview;
    const isStepSkipped = isStepFinished && !isStepCompleted;

    return {
        completed: isStepCompleted,
        disabled: isStepSkipped,
        element: <StepOne isStepCompleted={isStepCompleted} isStepFinished={isStepFinished} />,
    };
};

const getStepTwo = (props) => {
    return {
        completed: false,
        element: <StepTwo {...props} />,
    };
};

const PreviewAndGrading = () => {
    const { currentAssignment, submissions, previewSubmissions } = useApp();
    const { performNext } = useJoyride();
    const { device } = useViewport();
    const { t } = useTranslation('assignment');

    const isOnboardingInProgress = currentAssignment.id === EXAMPLE_ASSIGNMENT_ID;
    const isLoading = !submissions || !previewSubmissions;
    const props = { currentAssignment };

    if (isOnboardingInProgress) {
        const hasSubmissions = Boolean(submissions?.length);

        return (
            <Stack spacing={8}>
                <Steps
                    steps={[
                        {
                            completed: false,
                            disabled: true,
                            element: (
                                <Stack>
                                    <Stack spacing={2}>
                                        <Stack
                                            alignItems={device === 'mobile' ? 'flex-start' : 'center'}
                                            direction={device === 'mobile' ? 'column-reverse' : 'row'}
                                            spacing={2}
                                        >
                                            <Text variant={'sh2'} weight={'semibold'}>
                                                {t('fine-tuning.title', 'Preview and Fine-Tune')}
                                            </Text>
                                            <Text component={'i'} color={'light'}>
                                                {t('common:ui.recommended', 'Recommended')}
                                            </Text>
                                        </Stack>
                                        <Box sx={{ height: '4px' }} />
                                    </Stack>
                                </Stack>
                            ),
                        },
                        {
                            completed: false,
                            element: (
                                <Stack spacing={5}>
                                    <Stack spacing={4}>
                                        <Text variant={'sh2'} weight={'semibold'}>
                                            {t('submission.title', 'Final Submission Grading & Feedback')}
                                        </Text>
                                        <GreyBackground spacing={3}>
                                            {!hasSubmissions ? (
                                                <>
                                                    <Button
                                                        sx={{ width: '100%' }}
                                                        className={'joyride-step-5'}
                                                        disabled={!submissions}
                                                        startIcon={<Icon kind={'upload'} />}
                                                        onClick={performNext}
                                                    >
                                                        {t('submission.upload-submission', 'Upload Submissions')}
                                                    </Button>
                                                    <Text color={'light'}>
                                                        {t(
                                                            'submission.description',
                                                            'Grades and feedback will be generated immediately after you upload. Please note, editing assignment details after upload is restricted to ensure consistent grades/feedback for all students. If you need to make changes, duplicate the assignment and edit the new version.'
                                                        )}
                                                    </Text>
                                                </>
                                            ) : (
                                                <Stack spacing={4}>
                                                    <WhiteBackground justifyContent={'space-between'} spacing={3}>
                                                        <Stack direction={'row'} spacing={2}>
                                                            <CustomIcon kind={'error'} />
                                                            <Text>
                                                                {t('submission.submission-status', 'Submission Status')}
                                                            </Text>
                                                        </Stack>
                                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                                            <Text>
                                                                {t('submission.pending-review', 'Pending review')}
                                                            </Text>
                                                            <Text weight={'semibold'}>1</Text>
                                                        </Stack>
                                                        <Stack direction={'row'} justifyContent={'space-between'}>
                                                            <Text>{t('common:ui.completed', 'Completed')}</Text>
                                                            <Text>0 / 1</Text>
                                                        </Stack>
                                                    </WhiteBackground>
                                                    <Button className={'joyride-step-6'} onClick={performNext}>
                                                        {t('submission.start-reviewing', 'Start Reviewing')}
                                                    </Button>
                                                    <Button disabled variant={'secondary'} onClick={() => {}}>
                                                        {t('submission.manage-submissions', 'Manage Submissions')}
                                                    </Button>
                                                    <Button
                                                        startIcon={<Icon kind={'upload'} />}
                                                        variant={'borderless'}
                                                        onClick={() => {}}
                                                    >
                                                        {t(
                                                            'submission.upload-additional-submissions',
                                                            'Upload Additional Submissions'
                                                        )}
                                                    </Button>
                                                    <Divider />
                                                    <Stack spacing={4}>
                                                        <Button
                                                            startIcon={<Icon kind={'upload'} />}
                                                            variant={'borderless'}
                                                            onClick={() => {}}
                                                        >
                                                            {t(
                                                                'submission.upload-additional-submissions',
                                                                'Upload Additional Submissions'
                                                            )}
                                                        </Button>
                                                        <Button
                                                            startIcon={<Icon kind={'refresh'} />}
                                                            variant={'borderless'}
                                                            color={'destructive'}
                                                            onClick={() => {}}
                                                        >
                                                            {t('submissions.reset-submissions', 'Reset Submissions')}
                                                        </Button>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </GreyBackground>
                                    </Stack>
                                </Stack>
                            ),
                        },
                    ]}
                />
            </Stack>
        );
    }

    if (isLoading) {
        return (
            <Stack spacing={3}>
                <Stack direction={'row'} spacing={device === 'mobile' ? 2 : 4}>
                    <Stack alignItems={'center'} spacing={3}>
                        <Skeleton variant={'circular'} width={24} height={24} />
                        <Box
                            sx={(theme) => ({
                                flex: 1,
                                width: '2px',
                                background: theme.palette.ensightful.neutral[700],
                                opacity: 0.4,
                            })}
                        />
                    </Stack>
                    <Stack sx={{ flex: 1 }} spacing={1}>
                        <Skeleton sx={{ fontSize: '0.75rem' }} width={40} />
                        <Stack spacing={4}>
                            <Stack spacing={2}>
                                <Skeleton sx={{ fontSize: '1rem' }} width={190} />
                                <Stack>
                                    <Skeleton sx={{ fontSize: '0.75rem' }} />
                                    <Skeleton sx={{ fontSize: '0.75rem' }} />
                                    <Skeleton sx={{ fontSize: '0.75rem' }} />
                                    <Skeleton sx={{ fontSize: '0.75rem' }} />
                                </Stack>
                            </Stack>
                            <Skeleton variant={'rounded'} height={36} />
                            <Skeleton sx={{ alignSelf: 'center' }} width={125} />
                        </Stack>
                    </Stack>
                </Stack>
                <Stack direction={'row'} spacing={device === 'mobile' ? 2 : 4}>
                    <Skeleton variant={'circular'} width={24} height={24} />
                    <Stack sx={{ flex: 1 }} spacing={1}>
                        <Skeleton sx={{ fontSize: '0.75rem' }} width={40} />
                        <Skeleton sx={{ fontSize: '1rem' }} width={device === 'mobile' ? '100%' : 325} />
                    </Stack>
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack spacing={8}>
            <Steps steps={[getStepOne(props), getStepTwo(props)]} />
        </Stack>
    );
};

const CustomStack = styled(Stack)(({ theme, device }) => {
    return {
        borderRadius: '8px',
        padding: device === 'mobile' ? theme.spacing(5, 3) : theme.spacing(5),
        background: theme.palette.ensightful.neutral[50],
    };
});

const GreyBackground = styled(Stack)(({ theme }) => {
    return {
        borderRadius: '16px',
        padding: theme.spacing(5),
        background: theme.palette.ensightful.neutral[50],
    };
});

const WhiteBackground = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(3),
        background: theme.palette.ensightful.shades[0],
        borderRadius: '8px',
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        padding: theme.spacing(0.75),
        background: theme.palette.ensightful.primary[500],
        color: theme.palette.ensightful.shades[0],
    };
});

export default PreviewAndGrading;
